import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, RefreshCw, Ban, CheckCircle2, Eye, Search, KeyRound, UserCog } from 'lucide-react';
import api from '../../utils/api';
import Modal, { ConfirmModal, Field } from '../../components/Modal';
import { STATUS_STYLES, formatINR, formatDate, daysLabel, errMsg } from './saUtils';

const EMPTY = {
  name: '', contact_person: '', email: '', phone: '', address: '', gst_number: '', domain: '',
  package_id: '', status: 'active', start_date: '', expiry_date: '', max_users: '', notes: '',
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

export default function SuperAdminOrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);
  const [renew, setRenew] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, danger, action }
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/superadmin/organizations'), api.get('/superadmin/packages')])
      .then(([o, p]) => { setOrgs(o.data); setPackages(p.data); })
      .catch(err => toast.error(errMsg(err, 'Could not load data')))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const pkgById = id => packages.find(p => String(p.id) === String(id));

  const saveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    for (const k of Object.keys(EMPTY)) payload[k] = form[k] === '' ? null : form[k];
    payload.name = String(form.name || '').trim();
    try {
      if (form.id) await api.put(`/superadmin/organizations/${form.id}`, payload);
      else await api.post('/superadmin/organizations', payload);
      toast.success(form.id ? 'Organization updated' : 'Organization created');
      setForm(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const doRenew = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(`/superadmin/organizations/${renew.org.id}/renew`, {
        package_id: renew.package_id, amount: renew.amount, duration_days: renew.duration_days,
        start_date: renew.start_date || null, payment_mode: renew.payment_mode, reference: renew.reference, notes: renew.notes,
      });
      toast.success(res.data.message);
      setRenew(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const setStatus = (org, status) => setConfirm({
    title: status === 'suspended' ? `Suspend ${org.name}?` : `Activate ${org.name}?`,
    message: status === 'suspended'
      ? (org.is_primary ? 'All users of this live organization (admin, workers, clients) will be logged out immediately and will not be able to log in.' : 'The organization will be suspended.')
      : 'The organization will be active again.',
    danger: status === 'suspended',
    label: status === 'suspended' ? 'Suspend' : 'Activate',
    action: async () => {
      await api.patch(`/superadmin/organizations/${org.id}/status`, { status });
      toast.success(status === 'suspended' ? 'Suspended' : 'Activated');
    },
  });

  const remove = org => setConfirm({
    title: `Delete ${org.name}?`,
    message: 'The organization and its subscription history will be permanently deleted.',
    danger: true,
    label: 'Delete',
    action: async () => { await api.delete(`/superadmin/organizations/${org.id}`); toast.success('Deleted'); },
  });

  const runConfirm = async () => {
    setSaving(true);
    try { await confirm.action(); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); setConfirm(null); }
  };

  const openRenew = org => {
    const pkg = pkgById(org.package_id) || packages.find(p => p.is_active);
    setRenew({
      org, package_id: pkg?.id || '', amount: pkg?.price ?? '', duration_days: pkg?.duration_days || '',
      start_date: '', payment_mode: 'UPI', reference: '', notes: '',
    });
  };

  const openDetail = async org => {
    try {
      const [d, u] = await Promise.all([
        api.get(`/superadmin/organizations/${org.id}`),
        org.is_primary ? api.get(`/superadmin/organizations/${org.id}/users`) : Promise.resolve({ data: null }),
      ]);
      setDetail({ ...d.data, users: u.data, tab: 'info' });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const filtered = orgs.filter(o => !q || [o.name, o.contact_person, o.email, o.phone, o.domain].some(v => String(v || '').toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-5 max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Organizations</h1>
          <p className="text-sm text-slate-500">Subscribers — package, expiry and access control</p>
        </div>
        <button className="btn-primary" onClick={() => setForm({ ...EMPTY })}><Plus size={16} /> New organization</button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="field-input !pl-9" placeholder="Search name, contact, phone…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Expiry</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(o => {
                const limit = o.max_users ?? o.package_max_users;
                return (
                  <tr key={o.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 flex items-center gap-2">
                        {o.name}
                        {!!o.is_primary && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">LIVE</span>}
                      </p>
                      <p className="text-xs text-slate-500">{[o.contact_person, o.phone].filter(Boolean).join(' · ') || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{o.package_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3">
                      <p className="text-slate-700">{formatDate(o.expiry_date)}</p>
                      <p className={`text-xs ${o.days_left != null && o.days_left <= 15 ? 'text-amber-700' : 'text-slate-500'}`}>{daysLabel(o.days_left)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {o.is_primary ? `${o.users_used ?? '—'} / ${limit ?? '∞'}` : (limit ? `max ${limit}` : '∞')}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={o.effective_status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button className="btn-ghost !p-2" title="Details" onClick={() => openDetail(o)}><Eye size={15} /></button>
                        <button className="btn-ghost !p-2" title="Assign / renew package" onClick={() => openRenew(o)}><RefreshCw size={15} /></button>
                        <button className="btn-ghost !p-2" title="Edit" onClick={() => setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, o[k] ?? ''])), id: o.id })}><Pencil size={15} /></button>
                        {o.status === 'suspended'
                          ? <button className="btn-ghost !p-2 hover:!text-emerald-600" title="Activate" onClick={() => setStatus(o, 'active')}><CheckCircle2 size={15} /></button>
                          : <button className="btn-ghost !p-2 hover:!text-red-600" title="Suspend" onClick={() => setStatus(o, 'suspended')}><Ban size={15} /></button>}
                        {!o.is_primary && <button className="btn-ghost !p-2 hover:!text-red-600" title="Delete" onClick={() => remove(o)}><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No organizations found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit */}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Edit organization' : 'New organization'}
        size="lg"
        footer={<>
          <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancel</button>
          <button type="submit" form="org-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </>}
      >
        {form && (
          <form id="org-form" onSubmit={saveOrg}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              <Field label="Organization name" required>
                <input className="field-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Contact person">
                <input className="field-input" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input className="field-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Email">
                <input className="field-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="GST number">
                <input className="field-input" value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value })} />
              </Field>
              <Field label="Domain / URL">
                <input className="field-input" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="moji.org.in" />
              </Field>
              <Field label="Package" hint={!form.id ? 'If expiry is empty, it is set from the package duration' : undefined}>
                <select className="field-input" value={form.package_id ?? ''} onChange={e => setForm({ ...form, package_id: e.target.value })}>
                  <option value="">— No package —</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({formatINR(p.price)} / {p.duration_days}d)</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className="field-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                </select>
              </Field>
              <Field label="Start date">
                <input className="field-input" type="date" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </Field>
              <Field label="Expiry date" hint="Leave empty = never expires">
                <input className="field-input" type="date" value={form.expiry_date || ''} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
              </Field>
              <Field label="Max users (override)" hint="Leave empty = use package limit">
                <input className="field-input" type="number" min="1" value={form.max_users ?? ''} onChange={e => setForm({ ...form, max_users: e.target.value })} />
              </Field>
            </div>
            <Field label="Address">
              <input className="field-input" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
            </Field>
            <Field label="Notes">
              <textarea className="field-input" rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </form>
        )}
      </Modal>

      {/* Renew / assign package */}
      <Modal
        open={!!renew}
        onClose={() => setRenew(null)}
        title="Assign / renew package"
        subtitle={renew ? `${renew.org.name} · current expiry ${formatDate(renew.org.expiry_date)}` : ''}
        footer={<>
          <button type="button" className="btn-secondary" onClick={() => setRenew(null)}>Cancel</button>
          <button type="submit" form="renew-form" className="btn-primary" disabled={saving || !renew?.package_id}>{saving ? 'Saving…' : 'Confirm'}</button>
        </>}
      >
        {renew && (
          <form id="renew-form" onSubmit={doRenew}>
            {packages.length === 0 && <p className="text-sm text-red-600 mb-3">Create a package on the Packages page first.</p>}
            <Field label="Package" required>
              <select className="field-input" required value={renew.package_id} onChange={e => {
                const p = pkgById(e.target.value);
                setRenew({ ...renew, package_id: e.target.value, amount: p?.price ?? '', duration_days: p?.duration_days || '' });
              }}>
                <option value="">— Select —</option>
                {packages.filter(p => p.is_active || String(p.id) === String(renew.package_id)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount received (₹)">
                <input className="field-input" type="number" min="0" step="0.01" value={renew.amount} onChange={e => setRenew({ ...renew, amount: e.target.value })} />
              </Field>
              <Field label="Duration (days)">
                <input className="field-input" type="number" min="1" value={renew.duration_days} onChange={e => setRenew({ ...renew, duration_days: e.target.value })} />
              </Field>
              <Field label="Start date" hint="Leave empty = from today / after current expiry">
                <input className="field-input" type="date" value={renew.start_date} onChange={e => setRenew({ ...renew, start_date: e.target.value })} />
              </Field>
              <Field label="Payment mode">
                <select className="field-input" value={renew.payment_mode} onChange={e => setRenew({ ...renew, payment_mode: e.target.value })}>
                  {['UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Free / Complimentary'].map(m => <option key={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Reference / transaction ID">
              <input className="field-input" value={renew.reference} onChange={e => setRenew({ ...renew, reference: e.target.value })} />
            </Field>
            <Field label="Notes">
              <input className="field-input" value={renew.notes} onChange={e => setRenew({ ...renew, notes: e.target.value })} />
            </Field>
          </form>
        )}
      </Modal>

      {/* Details */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name} subtitle={detail?.is_primary ? 'Live organization — login on this URL' : undefined} size="xl">
        {detail && <OrgDetail detail={detail} setDetail={setDetail} />}
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
        loading={saving}
        danger={confirm?.danger}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.label}
      />
    </div>
  );
}

function OrgDetail({ detail, setDetail }) {
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [renameFor, setRenameFor] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const tabs = [['info', 'Info'], ['history', `Subscriptions (${detail.subscriptions.length})`]];
  if (detail.users) tabs.push(['users', `Users (${detail.users.length})`]);

  const toggleUser = async (u) => {
    try {
      await api.patch(`/superadmin/organizations/${detail.id}/users/${u.id}/status`, { is_active: !u.is_active });
      setDetail({ ...detail, users: detail.users.map(x => x.id === u.id ? { ...x, is_active: u.is_active ? 0 : 1 } : x) });
      toast.success(u.is_active ? 'User deactivated' : 'User activated');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/superadmin/organizations/${detail.id}/users/${resetFor.id}/password`, { password: newPw });
      toast.success(`Password reset for ${resetFor.username}`);
      setResetFor(null); setNewPw('');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changeUsername = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put(`/superadmin/organizations/${detail.id}/users/${renameFor.id}/username`, { username: newUsername.trim() });
      setDetail({ ...detail, users: detail.users.map(x => x.id === renameFor.id ? { ...x, username: res.data.username } : x) });
      toast.success(res.data.message);
      setRenameFor(null); setNewUsername('');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const info = [
    ['Status', <StatusBadge status={detail.effective_status} />],
    ['Package', detail.package_name || '—'],
    ['Start date', formatDate(detail.start_date)],
    ['Expiry', `${formatDate(detail.expiry_date)} (${daysLabel(detail.days_left)})`],
    ['Max users', detail.max_users ?? detail.package_max_users ?? 'Unlimited'],
    ['Contact person', detail.contact_person || '—'],
    ['Phone', detail.phone || '—'],
    ['Email', detail.email || '—'],
    ['GST', detail.gst_number || '—'],
    ['Domain', detail.domain || '—'],
    ['Address', detail.address || '—'],
    ['Notes', detail.notes || '—'],
  ];

  return (
    <div>
      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setDetail({ ...detail, tab: k })}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${detail.tab === k ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {label}
          </button>
        ))}
      </div>

      {detail.tab === 'info' && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {info.map(([k, v]) => (
            <div key={k}><dt className="text-xs font-semibold text-slate-500">{k}</dt><dd className="text-slate-800 mt-0.5">{v}</dd></div>
          ))}
        </dl>
      )}

      {detail.tab === 'history' && (
        detail.subscriptions.length === 0 ? <p className="text-sm text-slate-500">No subscription records.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase text-slate-500">
                <th className="py-2 pr-3">Package</th><th className="py-2 pr-3">Period</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Mode</th><th className="py-2">Reference</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {detail.subscriptions.map(s => (
                  <tr key={s.id}>
                    <td className="py-2 pr-3 font-medium">{s.package_name}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{formatDate(s.start_date)} → {formatDate(s.end_date)}</td>
                    <td className="py-2 pr-3">{formatINR(s.amount)}</td>
                    <td className="py-2 pr-3">{s.payment_mode || '—'}</td>
                    <td className="py-2">{s.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {detail.tab === 'users' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-bold uppercase text-slate-500">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Username</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Status</th><th className="py-2 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {detail.users.map(u => (
                <tr key={u.id}>
                  <td className="py-2 pr-3 font-medium">{u.name}</td>
                  <td className="py-2 pr-3">{u.username}</td>
                  <td className="py-2 pr-3 capitalize">{u.role}</td>
                  <td className="py-2 pr-3">{u.is_active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                  <td className="py-2">
                    <div className="flex justify-end gap-1">
                      <button className="btn-ghost !p-1.5" title="Change username" onClick={() => { setRenameFor(u); setNewUsername(u.username); setResetFor(null); }}><UserCog size={14} /></button>
                      <button className="btn-ghost !p-1.5" title="Reset password" onClick={() => { setResetFor(u); setNewPw(''); setRenameFor(null); }}><KeyRound size={14} /></button>
                      <button className={`btn-ghost !p-1.5 ${u.is_active ? 'hover:!text-red-600' : 'hover:!text-emerald-600'}`} title={u.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleUser(u)}>
                        {u.is_active ? <Ban size={14} /> : <CheckCircle2 size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renameFor && (
            <form onSubmit={changeUsername} className="mt-4 p-3 rounded-xl bg-slate-50 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <label className="field-label">New username for {renameFor.name} (currently {renameFor.username})</label>
                <input className="field-input" type="text" minLength={3} required value={newUsername} onChange={e => setNewUsername(e.target.value)} autoFocus />
                <p className="text-xs text-slate-500 mt-1">The user must log in with the new username from now on. Password stays the same.</p>
              </div>
              <button type="button" className="btn-secondary" onClick={() => setRenameFor(null)}>Cancel</button>
              <button type="submit" className="btn-primary">Change</button>
            </form>
          )}
          {resetFor && (
            <form onSubmit={resetPassword} className="mt-4 p-3 rounded-xl bg-slate-50 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <label className="field-label">New password for {resetFor.username}</label>
                <input className="field-input" type="text" minLength={4} required value={newPw} onChange={e => setNewPw(e.target.value)} autoFocus />
              </div>
              <button type="button" className="btn-secondary" onClick={() => setResetFor(null)}>Cancel</button>
              <button type="submit" className="btn-primary">Reset</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
